import Decimal from 'decimal.js';
import { useIntl } from 'react-intl';

import { Currency } from '../../types/currency';
import { ISSUE_LTV } from '../utils/calculatorConfig';
import { ABBREVIATE_THRESHOLD, abbreviateNumber } from '../utils/abbreviate';
import { t } from '../utils/translate';
import { InfoTooltip } from './InfoTooltip';
import styles from './LoanDetailsPanel.module.less';

interface Props {
  currency: Currency;
  interestRate: Decimal;
  maturityRepaymentValue: Decimal;
  originationFeeRate: string;
  isMobile: boolean;
}

// On mobile, fiat values ≥ 100,000 collapse to k/M to prevent overflow per spec.
function formatFiat(value: Decimal, locale: string, isMobile: boolean): string {
  if (isMobile && value.abs().gte(ABBREVIATE_THRESHOLD)) {
    return abbreviateNumber(value, locale);
  }
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
  }).format(Number(value.toFixed(0)));
}

function formatPercent(value: Decimal, locale: string): string {
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  }).format(Number(value.toFixed(2)));
}

export function LoanDetailsPanel({
  currency,
  interestRate,
  maturityRepaymentValue,
  originationFeeRate,
  isMobile,
}: Props) {
  const intl = useIntl();
  const locale = intl.locale;

  const rateText = t(intl, 'calculator.interestRateValue', {
    rate: formatPercent(interestRate, locale),
  });
  const ltvText = `${ISSUE_LTV.mul(100).toFixed(0)}%`;
  const repaymentText = formatFiat(maturityRepaymentValue, locale, isMobile);
  const feeText = t(intl, 'calculator.originationFeeFootnote', {
    rate: originationFeeRate,
  });

  return (
    <aside
      className={styles.panel}
      aria-label={t(intl, 'calculator.loanDetails')}
    >
      <div className={styles.title}>
        {t(intl, 'calculator.loanDetails')}
      </div>

      <div className={styles.stat}>
        <div className={styles.statHead}>
          <span className={styles.statLabel}>
            {t(intl, 'calculator.interestRate')}
          </span>
          <InfoTooltip
            ariaLabel={t(intl, 'calculator.interestRateInfo')}
            content={t(intl, 'calculator.interestRateTooltip')}
          />
        </div>
        <div className={styles.statValue}>
          <span className={styles.greyed}>~</span>
          <span>{' '}</span>
          <span>{rateText}</span>
        </div>
      </div>

      <div className={styles.stat}>
        <div className={styles.statHead}>
          <span className={styles.statLabel}>
            {t(intl, 'calculator.ltv')}
          </span>
        </div>
        <div className={styles.statValue}>{ltvText}</div>
      </div>

      <div className={styles.stat}>
        <div className={styles.statHead}>
          <span className={styles.statLabel}>
            {t(intl, 'calculator.maturityRepayment')}
          </span>
          <InfoTooltip
            ariaLabel={t(intl, 'calculator.maturityRepaymentInfo')}
            content={feeText}
          />
        </div>
        <div className={styles.statValue}>
          <span>{repaymentText}</span>
          <span>{' '}</span>
          <span className={styles.greyed}>{currency}</span>
        </div>
      </div>
    </aside>
  );
}
